import {detectBaseUrl} from "../utils/utils";

const createBrowserSpeechRecognition = (initialCallback) => {

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const baseUrl = detectBaseUrl();
    let recognition = null;
    let onMessageCallback = initialCallback;
    let ws = null;
    let isRecognizing = false;
    let isBrowserSpeech = true;
    let isModalOpen = false;
    let previousModalState = null;
    let audioContext = null;
    let processor = null;
    let input = null;
    let stream = null;
    let idleTimer = null;
    const IDLE_TIMEOUT = 2 * 60 * 1000;
    let wakeWordDetected = false;
    let isConnecting = false;
    let pendingConnectionPromise = null;
    let noSpeechRetryCount = 0;
    let noSpeechRestartTimer = null;
    const NO_SPEECH_RETRY_DELAYS_MS = [120, 280, 550];

    const clearNoSpeechRestart = () => {
        if (noSpeechRestartTimer) {
            clearTimeout(noSpeechRestartTimer);
            noSpeechRestartTimer = null;
        }
    };

    const startIdleTimer = () => {
        clearIdleTimer();
        idleTimer = setTimeout(() => {
            console.warn("WebSocket idle timeout reached, closing connection");
            closeWebSocket();
        }, IDLE_TIMEOUT);
    };

    const clearIdleTimer = () => {
        if (idleTimer) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }
    };


    const sendSessionMeta = () => {
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({
            isModalOpen,
            isBrowserSpeech,
            transcriptionService: window.__VOICE_SEARCH_ENV_SETTINGS__.TRANSCRIPTION_SERVICE,
        }));
        previousModalState = isModalOpen;
    };

    const setupWebSocketAsync = async () => {
        if (ws && ws.readyState === WebSocket.OPEN) return ws;

        if (pendingConnectionPromise) {
            return pendingConnectionPromise;
        }

        const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL;
        if (!websocketUrl) {
            throw new Error("WebSocket URL is not defined");
        }

        pendingConnectionPromise = new Promise((resolve, reject) => {
            isConnecting = true;
            ws = new WebSocket(websocketUrl);

            ws.onopen = () => {
                isConnecting = false;
                startIdleTimer();
                try {
                    sendSessionMeta();
                } catch (e) {
                    console.error("Failed to send initial modal state on WebSocket open:", e);
                }
                resolve(ws);
            };

            ws.onmessage = (msg) => {
                startIdleTimer();
                const data = JSON.parse(msg.data);
                let text = '';
                let hold = null;
                if (data.type === "transcription" && data.text) {
                    text = data.text;
                } else if (data.text) {
                    text = data.text;
                } else if (data.transcription) {
                    text = data.transcription;
                }
                if (data.hold !== undefined) {
                    hold = data.hold;
                }
                if (hold === true && !wakeWordDetected) {
                    wakeWordDetected = true;
                    onMessageCallback({text, hold});
                    startBrowserRecognition();
                } else if (hold === false) {
                    onMessageCallback({text, hold});
                }
            };

            ws.onerror = (error) => {
                isConnecting = false;
                reject(error);
            };

            ws.onclose = () => {
                isConnecting = false;
                clearIdleTimer();
                ws = null;
                try {
                    if (typeof onMessageCallback === 'function') {
                        onMessageCallback({text: '', hold: false});
                    }
                } catch (e) {
                    console.error('Error handling WebSocket onclose cleanup:', e);
                }
            };
        }).finally(() => {
            pendingConnectionPromise = null;
        });

        return pendingConnectionPromise;
    };

    const sendAudioData = async (audioData) => {
        if (isConnecting) return;
        try {
            await setupWebSocketAsync();
        } catch (error) {
            console.error("Failed to establish WebSocket connection:", error);
            return;
        }

        if (!ws || ws.readyState !== WebSocket.OPEN) return;

        startIdleTimer();
        if (audioData instanceof ArrayBuffer) {
            if (isModalOpen !== previousModalState) {
                sendSessionMeta();
            }
            ws.send(audioData);
            return;
        }
        ws.send(audioData);
    };

    // =============================
    // AUDIO CAPTURE FOR WAKE WORD
    // =============================

    const startWakeWordListening = async () => {
        if (audioContext) return;
        await setupWebSocketAsync();
        stream = await navigator.mediaDevices.getUserMedia({audio: true});
        audioContext = new AudioContext({sampleRate: 16000});
        await audioContext.audioWorklet.addModule(`${baseUrl}/VoiceDetectionProcessor.js`);
        input = audioContext.createMediaStreamSource(stream);
        processor = new AudioWorkletNode(audioContext, 'voice-detection-processor');
        processor.port.onmessage = (event) => {
            if (event.data.type === "audioData") {
                sendAudioData(event.data.data);
            }
        };

        input.connect(processor);
        processor.connect(audioContext.destination);
    };

    // =============================
    // BROWSER SPEECH RECOGNITION
    // =============================

    const setupRecognition = () => {
        if (!SpeechRecognition) return null;
        if (recognition) return recognition;
        recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = "en-US";
        recognition.onresult = (event) => {
            noSpeechRetryCount = 0;
            let transcript = "";
            let hold = false;
            for (let i = event.resultIndex; i < event.results.length; i++) {
                transcript += event.results[i][0].transcript;
                hold = !event.results[i].isFinal;
            }
            if (transcript) {
                onMessageCallback({text: 'Reco ' + transcript, hold});
            }
        };
        recognition.onend = async () => {
            if (!lastError) {
                isRecognizing = false;
                wakeWordDetected = false;
                noSpeechRetryCount = 0;
                clearNoSpeechRestart();
                await resumeWakeWordListening();
            } else {
                console.warn("Recognition ended due to error:", lastError);
            }
        };

        let lastError = null;

        const scheduleNoSpeechRestart = () => {
            const retryDelay = NO_SPEECH_RETRY_DELAYS_MS[noSpeechRetryCount];
            if (retryDelay === undefined || !wakeWordDetected) {
                return false;
            }

            clearNoSpeechRestart();
            noSpeechRestartTimer = setTimeout(async () => {
                noSpeechRestartTimer = null;
                if (!recognition || isRecognizing || !wakeWordDetected) return;
                try {
                    recognition.start();
                    isRecognizing = true;
                    lastError = null;
                    noSpeechRetryCount += 1;
                    console.warn("Browser STT auto-restart after no-speech, attempt:", noSpeechRetryCount);
                } catch (e) {
                    console.error("Browser STT no-speech restart failed:", e);
                    wakeWordDetected = false;
                    noSpeechRetryCount = 0;
                    await resumeWakeWordListening();
                }
            }, retryDelay);

            return true;
        };

        recognition.onerror = async (event) => {
            lastError = event.error;
            console.error("Browser STT error:", lastError);
            isRecognizing = false;

            if (lastError === "no-speech" && scheduleNoSpeechRestart()) {
                return;
            }

            clearNoSpeechRestart();
            noSpeechRetryCount = 0;
            wakeWordDetected = false;
            recognition.stop();
            await resumeWakeWordListening();
        };

        recognition.onclose = () => {
            if (lastError) {
                console.error("Recognition closed after error:", lastError);
            }
        };
        return recognition;
    };

    const startBrowserRecognition = async () => {
        setupRecognition();
        if (!recognition || isRecognizing) return;
        clearNoSpeechRestart();
        noSpeechRetryCount = 0;
        await pauseWakeWordListening();
        try {
            recognition.start();
            isRecognizing = true;
        } catch (e) {
            isRecognizing = false;
            await resumeWakeWordListening();
            console.error("startBrowserRecognition Error:", e);
        }
    };

    // =============================
    // PUBLIC API
    // =============================

    const startRecognition = async () => {
        await startWakeWordListening();
        return true;
    };

    const stopRecognition = () => {
        clearNoSpeechRestart();
        noSpeechRetryCount = 0;
        wakeWordDetected = false;
        if (recognition && isRecognizing) {
            recognition.stop();
            isRecognizing = false;
        }
    };

    const closeRecognition = () => {
        stopRecognition();
        clearNoSpeechRestart();
        if (processor) processor.disconnect();
        if (input) input.disconnect();
        if (stream) {
            stream.getTracks().forEach(t => t.stop());
        }
        if (audioContext) audioContext.close();
        if (ws) ws.close();
        audioContext = null;
        processor = null;
        input = null;
        stream = null;
        ws = null;
    };

    const closeWebSocket = () => {
        clearIdleTimer();
        pendingConnectionPromise = null;
        isConnecting = false;
        if (ws) {
            try {
                ws.close();
            } catch (e) {
                console.error("Error closing OpenAI WebSocket:", e);
            }
            ws = null;
        }
    };

    const updateCallback = (cb) => {
        onMessageCallback = cb;
    };

    const setModalState = (state) => {
        previousModalState = isModalOpen;
        isModalOpen = state;
    };

    const pauseWakeWordListening = async () => {
        if (processor) processor.disconnect();
        if (input) input.disconnect();
    };

    const resumeWakeWordListening = async () => {
        if (!stream || !audioContext || !processor || !input) return;
        input.connect(processor);
        processor.connect(audioContext.destination);
    };

    return {
        setupRecognition,
        startRecognition,
        stopRecognition,
        closeRecognition,
        updateCallback,
        setModalState,
        isConnected: () => ws && ws.readyState === WebSocket.OPEN,
        getWebSocket: () => ws,
    };
};

export default createBrowserSpeechRecognition;
